<template>
  <div class="row">
    <div class="col-lg-12 col-xxl-12">
      <div class="card card-custom card-stretch gutter-b">
        <div class="card-header border-0 py-5">
          <h3 class="card-title align-items-start flex-column">
            <span class="card-label font-weight-bolder text-dark">
              ประวัติสแกนแลกแต้ม
            </span>
            <span class="text-muted mt-2 font-weight-bold font-size-sm">
              ทั้งหมด {{ total.toLocaleString("th-TH") }} รายการ
            </span>
          </h3>
        </div>
        <div class="card-body pt-0 pb-3">
          <div class="col-lg-6 p-0 mb-4">
            <b-input-group size="sm">
              <b-form-input
                v-model="filter"
                :placeholder="searchPlaceholder"
                type="search"
                debounce="300"
              ></b-form-input>
              <b-input-group-append>
                <b-button :disabled="!filter" @click="filter = ''">{{
                  $t("PAGE.DEFAULT.TABLE.CLEAR")
                }}</b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <div class="table-responsive">
            <b-table
              id="campaign-qr-history-table"
              :items="items"
              :fields="visibleFields"
              :busy="isBusy"
              :borderless="true"
              head-variant="light"
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner small class="align-middle"></b-spinner>
                </div>
              </template>
              <template #cell(index)="data">
                <div class="text-right">
                  {{
                    (pagination.page - 1) * pagination.perPage + data.index + 1
                  }}
                </div>
              </template>
              <template #cell(created_at)="data">
                <span class="text-nowrap">{{
                  formatDateTime(data.item.created_at)
                }}</span>
              </template>
              <template #cell(customer)="data">
                <span>{{ customerName(data.item) }}</span>
              </template>
              <template #cell(phone_number)="data">
                <span>{{ data.item.phone_number || "-" }}</span>
              </template>
              <template #cell(point)="data">
                <span>{{
                  Number(data.item.point).toLocaleString("th-TH")
                }}</span>
              </template>
            </b-table>

            <p
              class="text-center text-muted pt-5"
              v-if="!isBusy && items.length < 1"
            >
              {{
                loadError
                  ? "โหลดข้อมูลไม่สำเร็จ"
                  : "ยังไม่มีข้อมูลประวัติสแกนแลกแต้ม"
              }}
            </p>
          </div>

          <div
            class="row justify-content-between align-items-center mt-3"
            v-if="total > 0"
          >
            <div class="col-auto">
              <b-form-group
                :label="$t('PAGE.DEFAULT.TABLE.PER_PAGE')"
                label-cols="auto"
                label-size="sm"
                class="mb-0 d-flex align-items-center"
              >
                <b-form-select
                  v-model="pagination.perPage"
                  size="sm"
                  :options="paginationOptions"
                  style="width: 80px"
                ></b-form-select>
              </b-form-group>
            </div>
            <div class="col-auto">
              <b-pagination
                v-model="pagination.page"
                :total-rows="total"
                :per-page="pagination.perPage"
                align="right"
                class="mb-0"
              ></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { formatDateTime } from "./qrRedeem.helper";

export default {
  name: "CampaignQrHistory",
  props: {
    campaignId: {
      type: [Number, String],
      required: true
    },
    customerCampaignId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      fields: [
        {
          key: "index",
          label: `${this.$t("PAGE.DEFAULT.TABLE.FIELDS.INDEX")}`
        },
        { key: "created_at", label: "วันเวลาที่สแกน" },
        { key: "qr_name", label: "ชื่อ QR" },
        { key: "customer", label: "ลูกค้า" },
        { key: "phone_number", label: "เบอร์โทร" },
        { key: "point", label: "แต้มที่ได้รับ" }
      ],
      items: [],
      total: 0,
      isBusy: false,
      loadError: false,
      filter: "",
      pagination: { page: 1, perPage: 20 },
      paginationOptions: [10, 20, 50, 100]
    };
  },
  computed: {
    isMemberHistory() {
      return this.customerCampaignId != null;
    },
    searchPlaceholder() {
      return this.isMemberHistory
        ? "ค้นหาชื่อ QR"
        : "ค้นหาชื่อ QR / ชื่อลูกค้า / เบอร์โทร";
    },
    visibleFields() {
      return this.isMemberHistory
        ? this.fields.filter(
            (field) => field.key !== "customer" && field.key !== "phone_number"
          )
        : this.fields;
    }
  },
  watch: {
    filter() {
      this.reloadFirstPage();
    },
    "pagination.perPage"() {
      this.reloadFirstPage();
    },
    customerCampaignId() {
      this.reloadFirstPage();
    },
    "pagination.page"() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    formatDateTime,
    customerName(row) {
      const full = `${row.first_name || ""} ${row.last_name || ""}`.trim();
      return full || row.display_name || "-";
    },
    reloadFirstPage() {
      if (this.pagination.page === 1) {
        this.fetchData();
      } else {
        this.pagination.page = 1;
      }
    },
    async fetchData() {
      this.isBusy = true;
      this.loadError = false;
      try {
        ApiService.setHeader();
        const { data } = await ApiService.getParams(
          `campaign/${this.campaignId}/qr_scan/pagination`,
          {
            params: {
              page: this.pagination.page,
              perPage: this.pagination.perPage,
              q: (this.filter || "").trim(),
              ...(this.customerCampaignId != null
                ? { customer_campaign_id: this.customerCampaignId }
                : {})
            }
          }
        );
        if (data.status === "success" && data.data) {
          this.items = data.data.data || [];
          this.total = Number(data.data.total) || 0;
        } else {
          throw new Error(data.error);
        }
      } catch (e) {
        this.loadError = true;
        this.items = [];
        this.total = 0;
      } finally {
        this.isBusy = false;
      }
    }
  }
};
</script>
